"use client";

import * as React from "react";
import { type Lang, COPY, renderEm } from "../copy.js";
import { EVENT_UTC_MS, GOOGLE_CALENDAR_URL, downloadIcs } from "../lib/calendar.js";
import { SectionEyebrow } from "./SectionEyebrow.js";

type Snapshot = { days: number; hours: number; mins: number; secs: number; live: boolean };

function snapshotAt(ms: number): Snapshot {
  const diff = Math.max(0, EVENT_UTC_MS - ms);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    mins: Math.floor((diff % 3600000) / 60000),
    secs: Math.floor((diff % 60000) / 1000),
    live: diff === 0,
  };
}

// On the server we have no real "now" we can match in hydration, so we render
// a static placeholder. After mount we start ticking with real values, and
// stop ticking once the event has started (live === true) — no point burning
// re-renders for someone who has the page open during/after the party.
function useCountdown() {
  const [snap, setSnap] = React.useState<Snapshot | null>(null);
  const live = snap?.live === true;
  React.useEffect(() => {
    if (live) return;
    const initial = snapshotAt(Date.now());
    setSnap(initial);
    if (initial.live) return;
    const id = setInterval(() => setSnap(snapshotAt(Date.now())), 1000);
    return () => clearInterval(id);
  }, [live]);
  return snap;
}

const pad = (n: number) => String(n).padStart(2, "0");

export function CountdownBlock({ lang }: { lang: Lang }) {
  const t = COPY[lang];
  const snap = useCountdown();

  return (
    <section className="block">
      <div className="wrap">
        <SectionEyebrow num={t.countdown.eyebrow.num} name={t.countdown.eyebrow.name} />
        <h2 className="hd">{renderEm(t.countdown.h)}</h2>
        {snap?.live ? (
          <p className="lead" style={{ marginTop: 20, fontStyle: "italic" }}>
            {t.countdown.live}
          </p>
        ) : (
          <div className="countdown" role="timer" aria-live="off">
            <div className="cell">
              <div className="num">{snap ? pad(snap.days) : "—"}</div>
              <div className="lbl">{t.countdown.days}</div>
            </div>
            <div className="cell">
              <div className="num">{snap ? pad(snap.hours) : "—"}</div>
              <div className="lbl">{t.countdown.hours}</div>
            </div>
            <div className="cell">
              <div className="num">{snap ? pad(snap.mins) : "—"}</div>
              <div className="lbl">{t.countdown.mins}</div>
            </div>
            <div className="cell">
              <div className="num">{snap ? pad(snap.secs) : "—"}</div>
              <div className="lbl">{t.countdown.secs}</div>
            </div>
          </div>
        )}
        <div className="venue-actions" style={{ marginTop: 22 }}>
          <a
            className="btn gold"
            href={GOOGLE_CALENDAR_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t.countdown.addGoogle} <span className="arrow">→</span>
          </a>
          <button type="button" className="btn" onClick={downloadIcs}>
            {t.countdown.addApple} <span className="arrow">↓</span>
          </button>
        </div>
      </div>
    </section>
  );
}
